/* eslint-disable no-nested-ternary */
import React, {
  useCallback, useContext, useEffect, useState,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Button } from '@mui/material';
import { Add, Upload } from '@mui/icons-material';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import NewMemberModal from './NewMemberModal';
import BulkImportMembersModal from './BulkImportMembersModal';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import './members.css';
import DeleteMemberConfirmationModal from './DeleteMemberConfirmationModal';
import NavigationLayout from '../../components/NavigationLayout';
import EditMemberModal from './EditMemberModal';
import Pagination from '../../components/pagination';
import useDebounce from '../../hooks/useDebounce';
import CustomTable from '../../components/table/CustomTable';
import MemberNameEmailColumn from '../../components/table/MemberNameEmailColumn';
import UserEditColumn from '../../components/table/UserEditColumn';
import SetupAlertModal from './SetupAlertModal';
import { formatDateWithTimezone } from '../../utils/time';

const TABLE_HEADERS = [
  {
    label: 'Name',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'name',
    colSpan: 1.5,
  },
  {
    label: 'Account Type',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'accountType',
  },
  {
    label: 'Reports To',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'reportingManager',
  },
  {
    label: 'Department',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'department',
  },
  {
    label: 'Country',
    sortable: false,
    sortIcon: UnfoldMore,
    sortKey: 'country',
  },
  {
    label: 'Designation',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'designation',
  },
  {
    label: 'Employment Start Date',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'employmentStartDate',
  },
  {
    label: 'Projects',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'projectCount',
  },
  {
    label: 'Status',
  },
  {
    label: 'Idle Tracking',
    sortable: false,
  },
  {
    label: 'Manual Tracker',
    sortable: false,
  },
  {
    label: '',
    colSpan: 0.2,
  },
];

const CustomTableStyles = {
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '16px',
    '.MuiTableSortLabel-iconDirectionAsc': {
      transform: 'rotate(0deg) !important',
    },
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
    border: 'none',
    '& td': {
      border: 'none',
    },
  },
};

function Members() {
  const { search } = useLocation();
  const history = useHistory();
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const { activeWorkspace } = useContext(UserContext);
  const [showNewMemberModal, setShowNewMemberModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [memberToEdit, setMemberToEdit] = useState(null);
  const [memberToSetupAlert, setMemberToSetupAlert] = useState(null);
  const [members, setMembers] = useState([]);
  const [reportingManagers, setReportingManagers] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalCounts, setTotalCounts] = useState(0);
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const { debounce } = useDebounce();

  const getDepartments = async () => {
    if (activeWorkspace) {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.departments(activeWorkspace.id),
      });

      if (response.success) {
        setDepartments(response.data?.departments || []);
      }
    }
  };

  const getDesignations = async () => {
    if (activeWorkspace) {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.designations(activeWorkspace.id),
      });

      if (response.success) {
        setDesignations(response.data?.designations || []);
      }
    }
  };

  const handleMemberUpdate = (updatedMember) => {
    setMembers((prevMembers) => prevMembers.map((m) => {
      if (m.id === updatedMember.id) {
        return { ...m, ...updatedMember };
      }
      return m;
    }));
  };

  const getMembers = useCallback(async (params) => {
    if (activeWorkspace) {
      setIsLoading(true);

      const [membersResponse, projectCountsResponse] = await Promise.all([
        sendRequest({
          method: 'get',
          url: API.WORKSPACE.members(activeWorkspace.id),
          params: {
            ...params,
          },
        }),
        sendRequest({
          method: 'get',
          url: API.WORKSPACE.memberProjectCounts(activeWorkspace.id),
        }),
      ]);

      if (membersResponse.success) {
        const projectCounts = projectCountsResponse.success ? projectCountsResponse.data : {};

        if (membersResponse.data?.members?.length) {
          const membersSet = new Set();
          const mappedMembers = membersResponse.data?.members.reduce((members, member) => {
            if (membersSet.has(member.id)) return members;

            const memberData = {
              name: (
              /* eslint-disable-next-line  */
                <div
                  onClick={() => { setMemberToEdit(member); }}
                  style={{ cursor: 'pointer' }}
                >
                  <MemberNameEmailColumn
                    profilePicture={member?.user?.profilePicture}
                    firstName={member.haveJoined ? member?.user?.firstName : ''}
                    lastName={member.haveJoined ? member?.user?.lastName : ''}
                    email={member?.user?.email}
                    id={member?.id}
                    component="div"
                  />
                </div>
              ),
              accountType: member.accountType,
              reportingManager: `${member.Manager?.ManagerUser?.firstName ?? ''} 
              ${member.Manager?.ManagerUser?.lastName ?? ''}`,
              department: member.department,
              country: member.user?.userLocation?.country,
              designation: member.designation,
              employmentStartDate: formatDateWithTimezone(
                member.employmentStartDate,
                activeWorkspace?.timezone,
              ),
              projectCount: projectCounts.data[member.id] || 0,
              status: member.haveJoined ? 'Active' : member.status ? 'Invited' : 'Deactivated',
              idleTracking: (
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: member.idleTimeTrackingEnabled ? '#e8f5e9' : '#ffebee',
                    color: member.idleTimeTrackingEnabled ? '#2e7d32' : '#c62828',
                  }}
                >
                  {member.idleTimeTrackingEnabled ? 'Enabled' : 'Disabled'}
                </span>
              ),
              manualTracker: (
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: member.manualTrackerEnabled ? '#e8f5e9' : '#ffebee',
                    color: member.manualTrackerEnabled ? '#2e7d32' : '#c62828',
                  }}
                >
                  {member.manualTrackerEnabled ? 'Enabled' : 'Disabled'}
                </span>
              ),
              actions: <UserEditColumn
                member={member}
                deactivateMember={() => setMemberToDelete(member)}
                editMember={() => setMemberToEdit(member)}
                setupAlert={() => setMemberToSetupAlert(member)}
                onMemberUpdate={handleMemberUpdate}
                style={{ float: 'right' }}
              />,
            };
            members.push(memberData);
            membersSet.add(member.id);
            return members;
          }, []);

          setMembers(mappedMembers);
        }
        setTotalPages(membersResponse.data?.pageInfo.totalPages ?? 1);
        setTotalCounts(membersResponse.data?.pageInfo.totalCounts ?? 0);
        setIsLoading(false);
      }
    }
  }, [activeWorkspace]);

  const getReportingMembers = useCallback(async () => {
    if (activeWorkspace) {
      const response = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.reportingManagers(activeWorkspace.id),
      });

      if (response.success) {
        setReportingManagers(response.data.reportingManagers);
      }
    }
  }, []);

  const fetchResults = async () => {
    const params = {
      page: currentPage,
      name: searchQuery,
      pageSize,
      sortKey,
      sortOrder,
    };
    await getMembers(params);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSearch = useCallback(
    debounce((query) => {
      setSearchQuery(query);
      setCurrentPage(1);
      history.push('?page=1');
    }, 500),
    [],
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    history.push(`?page=${page}`);
  };

  const handlePageSizeChange = (pageSize) => {
    setCurrentPage(1);
    setTotalPages(Math.ceil(totalCounts / pageSize));
    setPageSize(pageSize);
  };

  const handleSort = debounce((pSortKey, pSortOrder) => {
    setSortKey(pSortKey);
    setSortOrder(pSortOrder);
  }, 250);

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery, pageSize, sortKey, sortOrder]);

  useEffect(() => {
    getReportingMembers();
    getDepartments();
    getDesignations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {showNewMemberModal
        && (
          <NewMemberModal
            closeModal={() => setShowNewMemberModal(false)}
            getMembers={fetchResults}
            getReportingManagers={getReportingMembers}
            reportingManagers={reportingManagers}
          />
        )}
      {showBulkImportModal
        && (
          <BulkImportMembersModal
            closeModal={() => setShowBulkImportModal(false)}
            getMembers={fetchResults}
          />
        )}
      {memberToDelete
        && (
          <DeleteMemberConfirmationModal
            member={memberToDelete}
            getMembers={fetchResults}
            closeModal={() => setMemberToDelete(null)}
          />
        )}
      {memberToEdit
        && (
          <EditMemberModal
            member={memberToEdit}
            departments={departments}
            designations={designations}
            getMembers={fetchResults}
            getReportingManagers={getReportingMembers}
            closeModal={() => setMemberToEdit(null)}
            reportingManagers={reportingManagers}
          />
        )}
      {memberToSetupAlert
          && (
            <SetupAlertModal
              closeModal={() => setMemberToSetupAlert(null)}
              member={memberToSetupAlert}
              getMembers={fetchResults}
            />
          )}
      <NavigationLayout title="Workspace Members" subtitle="Manage members within your workspace">
        <div className="flex-row justify-content-between" style={{ marginTop: 28 }}>
          <div className="searchBox">
            <input
              type="text"
              placeholder="Search for user"
              onChange={(e) => handleSearch(e.target.value)}
              data-testid="search"
            />
            <i className="fa fa-search searchIcon" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setShowBulkImportModal(true);
              }}
              startIcon={<Upload fontSize="small" />}
              data-testid="import-users"
            >
              Import Users
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setShowNewMemberModal(true);
              }}
              startIcon={<Add fontSize="small" />}
              data-testid="new-member"
            >
              New Member
            </Button>
          </div>
        </div>
        <CustomTable
          headers={TABLE_HEADERS}
          items={members}
          loading={isLoading}
          handleSort={handleSort}
          styles={CustomTableStyles}
        />
        <Pagination
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={totalPages}
          totalCounts={totalCounts}
          onPageChange={handlePageChange}
          setPageSize={handlePageSizeChange}
          styles={{ margin: 0 }}
        />
      </NavigationLayout>
    </div>
  );
}

export default Members;
